'use client';

import { Modal as FlowbiteModal } from 'flowbite-react';
import type { FunctionComponent } from 'react';

import AbbTabSelectorRed from '@/assets/icons/abb_tab-selector_red.svg';
import { AbbClose } from '@/assets/icons/components/abb-close';
import { useNavigation } from '@/modules/utils/custom-hooks/use-navigation';
import { useTheme } from '@/modules/utils/custom-hooks/use-theme';

import { Paragraph } from '../paragraph/paragraph';
import type { ModalProps } from './types/modal';

const customTheme = {
  root: {
    base: 'fixed inset-x-0 top-0 z-50 h-screen overflow-y-auto overflow-x-hidden md:inset-0 md:h-full relative',
    show: {
      on: '!flex !bg-black !bg-opacity-30',
      off: 'hidden'
    },
    sizes: {},
    positions: {}
  },
  content: {
    base: 'relative h-full min-w-[500px] lg:max-w-4xl md:max-w-2xl sm:max-w-md md:h-auto',
    inner: 'flex max-h-[90dvh] flex-col rounded-card bg-n50_hover shadow p-9 dark:bg-card_dark'
  }
};

const alignVariants = {
  left: 'justify-start',
  right: 'justify-end',
  center: 'justify-center'
};

export const Modal: FunctionComponent<ModalProps> = ({
  name,
  title,
  closeButton = false,
  contentWidth,
  onClose,
  children,
  className,
  titleStyle = '',
  withLine = false,
  ...props
}) => {
  const { getSearchParams, removeAllSearchParams } = useNavigation();
  const { isDarkTheme } = useTheme();
  const { modal: modalName } = getSearchParams();

  return (
    <FlowbiteModal
      {...props}
      theme={{ ...customTheme, content: { ...customTheme.content, inner: `${customTheme.content.inner} ${contentWidth ?? ''}` } }}
      show={modalName === name}
      onClose={() => {
        if (onClose) onClose();
        removeAllSearchParams();
      }}
      className={className}
    >
      {closeButton && (
        <AbbClose
          size={24}
          fill={isDarkTheme ? '#f1f1f1' : '#666666'}
          onClick={removeAllSearchParams}
          className='absolute top-6 right-6 cursor-pointer dark:text-n700_text_dark_mode'
        />
      )}
      {title ? (
        <div className={`flex items-start rounded-t border-0 ${titleStyle ? titleStyle : 'py-7'} ${alignVariants[title?.align ?? 'left']}`}>
          {withLine && <AbbTabSelectorRed className='absolute' />}
          <Paragraph className={withLine ? 'mt-1' : ''} variant={title?.variant ?? 'h4'}>
            {title?.text}
          </Paragraph>
        </div>
      ) : null}
      {children}
    </FlowbiteModal>
  );
};
